import { Table, Tag } from 'antd';
import { type ColumnFilterItem, type FilterDropdownProps } from 'antd/es/table/interface';
import { type TFunction } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type ListGiftVoucherDataFragment } from '../../../api/fragments';
import { useListGiftVouchersQuery, useGetGiftVouchersListFiltersQuery } from '../../../api/queries';
import { CompanyFilterListCollection, GiftVoucherStatus } from '../../../api/types';
import { useCompany, useCompanies } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import { getDateTimeOffsetFormat } from '../../../utilities/date';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useFormats from '../../../utilities/useFormats';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';
import useSortOrder, { getSortField } from './useSortOrder';

const getGiftVoucherStatusColor = (status: GiftVoucherStatus) => {
    switch (status) {
        case GiftVoucherStatus.Expired:
            return 'red';

        case GiftVoucherStatus.Unused:
            return 'green';

        case GiftVoucherStatus.Used:
            return 'grey';

        case GiftVoucherStatus.ApplicantDetailsReceived:
        case GiftVoucherStatus.PaymentCompleted:
        case GiftVoucherStatus.PaymentReceived:
            return 'blue';

        case GiftVoucherStatus.PaymentFailed:
        case GiftVoucherStatus.PaymentTimeout:
            return 'red';

        case GiftVoucherStatus.PaymentPending:
            return 'cyan';

        default:
            return '';
    }
};

const renderGiftVoucherStatus = (status: GiftVoucherStatus, t: TFunction) => {
    const color = getGiftVoucherStatusColor(status);

    const tagName = t(`giftVoucherList:status.${status}`);

    return (
        <Tag key={status} color={color}>
            {tagName}
        </Tag>
    );
};

type FormattedValueProps = {
    value: number;
    currency: string;
    numberOfBookingReferenceDays: number;
    amountDecimal: number;
};

const FormattedValue = ({ value, currency, numberOfBookingReferenceDays, amountDecimal }: FormattedValueProps) => {
    const { formatAmountWithCurrency } = useFormats(currency, amountDecimal, 0);

    return <>{formatAmountWithCurrency(value * numberOfBookingReferenceDays)}</>;
};

const GiftVoucherList = () => {
    const { t } = useTranslation('giftVoucherList');

    const searchBox = useSearchInDropDown();

    const navigate = useNavigate();

    // get company context
    const company = useCompany(true);
    const companies = useCompanies(true);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.GiftVoucher);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // fetch data
    const { page, pageSize, sort, filter } = state;

    // Wrap the filter
    const wrappedFilter = useMemo(() => {
        let defaultCompanyFilter: string[] = [];
        if (companies.length) {
            defaultCompanyFilter = companies.map(company => company.id);
        } else if (company) {
            defaultCompanyFilter = [company.id];
        }

        return {
            ...filter,
            companyIds: defaultCompanyFilter,
        };
    }, [companies, company, filter]);

    const { data, loading, error } = useListGiftVouchersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: sort ? { field: sort.field, order: sort.order } : null,
            filter: wrappedFilter,
        },
    });

    const {
        data: options,
        loading: optionsLoading,
        error: optionsError,
    } = useGetGiftVouchersListFiltersQuery({
        variables: {
            filter: {
                companyIds: wrappedFilter.companyIds,
            },
        },
    });

    const companyFilterList = useGetCompanyFilter({ collection: CompanyFilterListCollection.FinanceProducts });

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            giftCode: !filters.giftCode ? null : filters.giftCode[0],
                            gifteeName: !filters.giftee ? null : filters.giftee[0],
                            purchaserName: !filters.purchaser ? null : filters.purchaser[0],
                            status: !filters.status ? undefined : (filters.status as GiftVoucherStatus[]),
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    // prepare items as a data source
    const dataSource = useMemo(
        () => (data?.listGiftVouchers?.items || []).map(item => ({ ...item, key: item.id })),
        [data]
    );

    const statusList = useMemo(
        () =>
            (options?.filters?.statuses ?? []).map(status => ({
                text: t(`giftVoucherList:status.${status}`),
                value: status,
            })),
        [t, options]
    );

    const total = data?.listGiftVouchers?.count || 0;

    const {
        giftCodeSort,
        giftVoucherStatusSort,
        valueSort,
        companySort,
        purchasedDateSort,
        purchaserSort,
        gifteeSort,
    } = useSortOrder(sort);

    if (!loading && !optionsLoading) {
        if (error || optionsError) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: ListGiftVoucherDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/giftCodes/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            scroll={{ x: true }}
            state={state}
            tableName={t('giftVoucherList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={companySort}
                    title={t('giftVoucherList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                key="giftCode"
                dataIndex="giftCode"
                filterDropdown={searchBox.render(t('giftVoucherList:columns.giftCode'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.giftCode ? [filter.giftCode] : undefined}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                sortOrder={giftCodeSort}
                title={t('giftVoucherList:columns.giftCode')}
                fixed
                sorter
            />
            <Table.Column<ListGiftVoucherDataFragment>
                key="value"
                dataIndex="value"
                render={(value, record) => (
                    <FormattedValue
                        amountDecimal={record.module.company?.roundings?.amount?.decimals ?? 0}
                        currency={record.module.company.currency}
                        numberOfBookingReferenceDays={record.numberOfBookingReferenceDays}
                        value={value}
                    />
                )}
                sortOrder={valueSort}
                title={t('giftVoucherList:columns.value')}
                sorter
            />
            <Table.Column
                key="purchaser"
                dataIndex={['purchaser', 'fullName']}
                filterDropdown={searchBox.render(t('giftVoucherList:columns.purchaser'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.purchaserName ? [filter.purchaserName] : undefined}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                render={value => value}
                sortOrder={purchaserSort}
                title={t('giftVoucherList:columns.purchaser')}
                sorter
            />
            <Table.Column
                key="giftee"
                dataIndex={['giftee']}
                filterDropdown={searchBox.render(t('giftVoucherList:columns.giftee'))}
                filterIcon={renderSearchIcon}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                render={value => value}
                sortOrder={gifteeSort}
                title={t('giftVoucherList:columns.giftee')}
                sorter
            />
            <Table.Column
                key="purchasedDate"
                dataIndex="purchasedDate"
                render={(value, record: ListGiftVoucherDataFragment) =>
                    getDateTimeOffsetFormat(value, t, record.module.company.timeZone)
                }
                sortOrder={purchasedDateSort}
                title={t('giftVoucherList:columns.purchasedDate')}
                sorter
            />
            <Table.Column
                key="status"
                align="center"
                dataIndex="status"
                filterDropdown={props => showFilterDropDown(props, statusList)}
                render={value => renderGiftVoucherStatus(value, t)}
                sortOrder={giftVoucherStatusSort}
                title={t('giftVoucherList:columns.status')}
                width={120}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default GiftVoucherList;
